/* ----------------------------------------------------------------------------
 * Leads (paged list + mobile cards + quick update + full owner edit) — pure MODEL.
 * Dependency-free; unit-tested under `node --test --experimental-strip-types`.
 * Wire shapes of GET/PATCH /v1/outreach/leads[/{ref}] plus the card view-model
 * the LeadCard renders (what to show, which actions exist, tel:/mailto:/maps hrefs)
 * and the EDIT descriptor (sections -> fields) the LeadEditSheet renders.
 *
 * Every enumeration label/option comes from the central config
 * (./leads-config.ts) — never hard-coded here or in a component.
 * -------------------------------------------------------------------------- */

import { LEAD_OPTIONS, optionLabel, platformGroup, PLATFORM_GROUP_LABEL, type LeadOption, type LeadOptionConfig, type PlatformKind, type PlatformGroup } from './leads-config.ts';

export type { PlatformKind, LeadOption, LeadOptionConfig, PlatformGroup, PlatformVendor, LeadCategoryData, LeadCategorySource, LeadCategoryTerm, LeadCategorySeedItem } from './leads-config.ts';
export { LEAD_OPTIONS, DEFAULT_LEAD_OPTIONS, resolveLeadOptions, optionLabel, optionHelp, PLATFORM_VENDORS, PLATFORM_GROUP_LABEL, platformNames, platformGroup, vendorFor, LEAD_ACTIONS, callActionAllowed, LEAD_CATEGORY_DATA, leadCategoriesFor, leadCategoriesForLead, leadCategoryNameMatches, OTHER_CATEGORY } from './leads-config.ts';
import { LEAD_CATEGORY_DATA as CATEGORY_DATA, type LeadCategorySource as CategorySource } from './leads-config.ts';

/** LeadProjection::toApi() shape (whitelist — never carries internal columns). */
export interface LeadRecord {
  lead_ref: string;
  status: string;
  status_label: string;
  follow_status: string | null;
  owner: string | null;
  campaign: string | null;
  source_directory: string | null;
  source_url: string | null;
  business_name: string | null;
  practitioner_name: string | null;
  modality: string | null;
  city_region: string | null;
  country: string | null;
  business_email: string | null;
  email_source_note: string | null;
  website: string | null;
  instagram_handle: string | null;
  linkedin_url: string | null;
  facebook_url: string | null;
  other_social: string | null;
  business_size: string | null;
  fit_notes: string | null;
  next_action: string | null;
  next_action_date: string | null;
  last_activity_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  is_test_data: boolean;
  domain: string | null;
  captured_via: string | null;
  project_id: number | null;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  route_day: string | null;
  priority: string | null;
  booking_platform: string | null;
  booking_platform_kind: PlatformKind | null;
  platform_evidence_note: string | null;
  platform_evidence_url: string | null;
  decision_maker: string | null;
  visit_approach: string | null;
  visit_approach_label: string | null;
  verification: string | null;
  visit_date: string | null;
  outcome_note: string | null;
  subscribed: boolean | null;
  content_shoot_booked: boolean | null;
  import_row_ref: string | null;
  links: LeadLinks | null;
  /** Server-computed (IVG N1): valid address AND status/erasure allow contact AND not on the suppression list. */
  email_contactable: boolean;
  /** Server label for booking_platform_kind (the FE config is authoritative for display; this is a fallback). */
  platform_kind_label: string | null;
  /** item 7: EFFECTIVE category slugs (manual when set, else derived from the business type; never empty — "other"). */
  categories?: string[];
  /** auto = derived from the business type; manual = set by the owner. */
  categories_source?: 'auto' | 'manual';
  /** Display names of `categories` (the workspace list's current names). */
  category_labels?: string[];
}

export interface LeadLinks { maps?: string; search?: string; source?: string; listings?: string[] }

export interface LeadsPage {
  items: LeadRecord[];
  total: number;
  next_cursor: string | null;
  scope: { project_id: number | null; host: string | null };
  facets: Record<'status' | 'route_day' | 'priority' | 'booking_platform_kind' | 'visit_approach', Record<string, number>> & Partial<Record<'platform_name' | 'platform_group' | 'category', Record<string, number>>>;
}

export interface LeadChange { field: string; old: string | null; new: string | null }
export interface LeadEventRow {
  event_type: string; template_id: string | null; link_id: string | null; event_at: string; actor: string; detail: string | null;
  /** FIELD_UPDATED events: what changed (values already shortened server-side); null for other events. */
  changes?: LeadChange[] | null;
}
export interface LeadDetailResponse { lead: LeadRecord; events: LeadEventRow[] }

export interface LeadsQuery {
  projectId?: number;
  cursor?: string;
  limit?: number;
  q?: string;
  status?: string;
  route_day?: string;
  priority?: string;
  approach?: string;
  platform_kind?: string;
  /** canonical vendor name (server facet platform_name) */
  platform?: string;
  /** directory | software */
  platform_group?: string;
  /** item 7: one category slug (server facet `category`) */
  category?: string;
}

/** Quick-update payload (PATCH). null clears a nullable field. Unchanged quick sheet. */
export interface LeadQuickUpdate {
  status?: string | null;
  visit_approach?: string | null;
  next_action_date?: string | null;
  visit_date?: string | null;
  outcome_note?: string | null;
  subscribed?: boolean | null;
  content_shoot_booked?: boolean | null;
  booking_platform?: string | null;
  fit_notes?: string | null;
}

// ---- option lists (derived from the central config; kept as named exports for existing callers) ----
export const VISIT_APPROACH_OPTIONS: ReadonlyArray<LeadOption> = LEAD_OPTIONS.visitApproaches;
export const STATUS_OPTIONS: ReadonlyArray<LeadOption> = LEAD_OPTIONS.statuses;
export const PLATFORM_KIND_OPTIONS: ReadonlyArray<LeadOption<PlatformKind>> = LEAD_OPTIONS.platformKinds;
export const PRIORITY_OPTIONS: ReadonlyArray<LeadOption> = LEAD_OPTIONS.priorities;
export const PLATFORM_KIND_LABEL: Record<PlatformKind, string> = Object.fromEntries(LEAD_OPTIONS.platformKinds.map((o) => [o.value, o.label])) as Record<PlatformKind, string>;
export const PLATFORM_KIND_HELP: Record<PlatformKind, string> = Object.fromEntries(LEAD_OPTIONS.platformKinds.map((o) => [o.value, o.help ?? ''])) as Record<PlatformKind, string>;

export function statusLabel(status: string | null | undefined, fallback?: string | null): string {
  return optionLabel(STATUS_OPTIONS, status, fallback) ?? status ?? '—';
}

export function approachLabel(value: string | null | undefined): string | null {
  return VISIT_APPROACH_OPTIONS.find((a) => a.value === value)?.label ?? null;
}

export function platformKindLabel(kind: string | null | undefined): string {
  return optionLabel(PLATFORM_KIND_OPTIONS, kind ?? 'unknown') ?? PLATFORM_KIND_LABEL.unknown;
}

/** tel: href from a human phone ("0207 361 1000" -> "tel:02073611000"); null when unusable. */
export function telHref(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/[^\d+]/g, '');
  return digits.replace(/\D/g, '').length >= 7 ? `tel:${digits}` : null;
}

/** Lead statuses that must never be contacted by email (client-side mirror of the server rule; the
 *  server's `email_contactable` is authoritative and also covers the suppression list + erasure).
 *  `erasure_status` itself is an internal column and is never projected — `status` is the public signal. */
export const NO_EMAIL_CONTACT_STATUSES: ReadonlySet<string> = new Set(['SUPPRESSED', 'ERASED', 'OPTED_OUT', 'UNSUBSCRIBED']);

/** Display email (plain selectable text): trimmed `business_email`, null when empty. */
export function displayEmail(email: string | null | undefined): string | null {
  const e = (email ?? '').trim();
  return e ? e : null;
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** mailto: href that opens the user's OWN mail app — no platform send, no outreach event.
 *  null when there is no usable address, the lead is in a no-contact status, or the server says
 *  `contactable === false` (suppression list / erasure — IVG N1). */
export function mailtoHref(email: string | null | undefined, status: string | null | undefined, contactable?: boolean | null): string | null {
  const e = displayEmail(email);
  if (!e || !EMAIL_RX.test(e)) return null;
  if (status && NO_EMAIL_CONTACT_STATUSES.has(status)) return null;
  if (contactable === false) return null;
  return `mailto:${e}`;
}

/** Absolute http(s) URL for a website that may be scheme-less. */
export function websiteHref(website: string | null | undefined): string | null {
  if (!website) return null;
  const w = website.trim();
  if (!w) return null;
  return /^https?:\/\//i.test(w) ? w : `https://${w}`;
}

/** Display host for a URL ("https://www.x.co.uk/a" -> "x.co.uk"). */
export function displayHost(url: string | null | undefined): string | null {
  const href = websiteHref(url);
  if (!href) return null;
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return url ?? null;
  }
}

/** Maps link: the imported Google Maps target, else a search built from name + address. */
export function mapsHref(lead: Pick<LeadRecord, 'links' | 'business_name' | 'address' | 'postcode' | 'city_region'>): string | null {
  if (lead.links?.maps) return lead.links.maps;
  const q = [lead.business_name, lead.address ?? lead.postcode ?? lead.city_region].filter(Boolean).join(' ');
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
}

/** Today as YYYY-MM-DD (UTC) — the reference date for overdue follow-ups. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** "Mon 5 Oct" from ISO; null when absent/invalid. */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Is a follow-up overdue relative to `today` (YYYY-MM-DD)? */
export function isOverdue(nextActionDate: string | null | undefined, today: string): boolean {
  return !!nextActionDate && nextActionDate.slice(0, 10) < today;
}

export interface LeadCardModel {
  ref: string;
  title: string;
  category: string | null;
  area: string | null;
  postcode: string | null;
  platform: string;
  platformKind: PlatformKind;
  platformKindLabel: string;
  status: string;
  statusLabel: string;
  approachLabel: string | null;
  followUp: string | null;
  followUpOverdue: boolean;
  priority: string | null;
  routeDay: string | null;
  /** item 7: category names (chips). */
  categories: string[];
  /** Plain display email (null when none on file) — shown as selectable text even when the action is hidden. */
  email: string | null;
  actions: { website: string | null; tel: string | null; mail: string | null; maps: string | null };
  evidence: { url: string | null; note: string | null; verification: string | null; listings: string[] };
}

/** Everything a card shows (owner spec: business, category, area, platform, sales status, approach, next follow-up + actions). */
export function leadCardModel(lead: LeadRecord, today: string): LeadCardModel {
  const kind: PlatformKind = lead.booking_platform_kind ?? 'unknown';
  return {
    ref: lead.lead_ref,
    title: lead.business_name || lead.lead_ref,
    category: lead.modality,
    area: lead.city_region,
    postcode: lead.postcode,
    platform: lead.booking_platform || 'Unknown',
    platformKind: kind,
    platformKindLabel: platformKindLabel(kind),
    status: lead.status,
    statusLabel: statusLabel(lead.status, lead.status_label),
    approachLabel: lead.visit_approach_label ?? approachLabel(lead.visit_approach),
    followUp: shortDate(lead.next_action_date),
    followUpOverdue: isOverdue(lead.next_action_date, today),
    priority: lead.priority,
    routeDay: lead.route_day,
    categories: lead.category_labels ?? [],
    email: displayEmail(lead.business_email),
    actions: { website: websiteHref(lead.website), tel: telHref(lead.phone), mail: mailtoHref(lead.business_email, lead.status, lead.email_contactable), maps: mapsHref(lead) },
    evidence: {
      url: lead.platform_evidence_url,
      note: lead.platform_evidence_note,
      verification: lead.verification,
      listings: lead.links?.listings ?? [],
    },
  };
}

/** Build the GET /outreach/leads query (drops empty filters). */
export function leadsQueryParams(q: LeadsQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (q.projectId) out.project_id = q.projectId;
  if (q.cursor) out.cursor = q.cursor;
  out.limit = q.limit ?? 50;
  for (const k of ['q', 'status', 'route_day', 'priority', 'approach', 'platform_kind', 'platform', 'platform_group', 'category'] as const) {
    const v = q[k];
    if (v && v.trim()) out[k] = v.trim();
  }
  return out;
}

/** Only send fields the user actually changed (quick sheet). */
export function diffQuickUpdate(lead: LeadRecord, draft: LeadQuickUpdate): LeadQuickUpdate {
  const out: LeadQuickUpdate = {};
  if (draft.status !== undefined && draft.status !== lead.status) out.status = draft.status;
  if (draft.visit_approach !== undefined && (draft.visit_approach ?? null) !== (lead.visit_approach ?? null)) out.visit_approach = draft.visit_approach;
  if (draft.next_action_date !== undefined && (draft.next_action_date ?? null) !== (lead.next_action_date ?? null)) out.next_action_date = draft.next_action_date;
  if (draft.visit_date !== undefined && (draft.visit_date ?? null) !== (lead.visit_date ?? null)) out.visit_date = draft.visit_date;
  if (draft.outcome_note !== undefined && (draft.outcome_note ?? null) !== (lead.outcome_note ?? null)) out.outcome_note = draft.outcome_note;
  if (draft.subscribed !== undefined && (draft.subscribed ?? null) !== (lead.subscribed ?? null)) out.subscribed = draft.subscribed;
  if (draft.content_shoot_booked !== undefined && (draft.content_shoot_booked ?? null) !== (lead.content_shoot_booked ?? null)) out.content_shoot_booked = draft.content_shoot_booked;
  if (draft.booking_platform !== undefined && (draft.booking_platform ?? null) !== (lead.booking_platform ?? null)) out.booking_platform = draft.booking_platform;
  if (draft.fit_notes !== undefined && (draft.fit_notes ?? null) !== (lead.fit_notes ?? null)) out.fit_notes = draft.fit_notes;
  return out;
}

/* ============================================================================
 * FULL OWNER EDIT — descriptor + diff + client-side validation.
 * The server (LeadRepository::EDITABLE / LeadEditValidator) is the authority;
 * this mirror exists so the form can show the right control per field and catch
 * obvious slips before a round trip. INTERNAL_FIELDS are listed only so a test
 * can assert they NEVER appear in the form.
 * ========================================================================== */

export type EditFieldType = 'text' | 'email' | 'url' | 'tel' | 'date' | 'select' | 'bool' | 'textarea' | 'links';

export type EditableTextField =
  | 'business_name' | 'practitioner_name' | 'decision_maker' | 'modality' | 'city_region' | 'address' | 'postcode'
  | 'website' | 'business_email' | 'phone' | 'instagram_handle' | 'linkedin_url' | 'facebook_url' | 'other_social'
  | 'booking_platform' | 'booking_platform_kind' | 'platform_evidence_note' | 'platform_evidence_url' | 'verification'
  | 'priority' | 'route_day' | 'status' | 'visit_approach' | 'visit_date' | 'outcome_note' | 'next_action' | 'next_action_date'
  | 'fit_notes' | 'campaign';
export type EditableField = EditableTextField | 'links' | 'subscribed' | 'content_shoot_booked';

/** PATCH body for the full edit: null clears a nullable field; only changed keys are sent. */
export type LeadEditPatch = Partial<Record<EditableTextField, string | null>> & {
  subscribed?: boolean | null;
  content_shoot_booked?: boolean | null;
  links?: LeadLinks | null;
  /** item 7: manual category slugs; [] = back to automatic (derived from the business type). */
  categories?: string[];
};

export interface EditFieldDef {
  name: EditableField;
  label: string;
  type: EditFieldType;
  required?: boolean;
  /** Which central option list a select reads. */
  options?: keyof LeadOptionConfig;
  help?: string;
  placeholder?: string;
  maxLength?: number;
}
export interface EditSection { key: 'business' | 'booking' | 'sales'; title: string; blurb?: string; fields: EditFieldDef[] }

export const EDIT_SECTIONS: ReadonlyArray<EditSection> = [
  {
    key: 'business', title: 'Business', blurb: 'Who and where they are.',
    fields: [
      { name: 'business_name', label: 'Business name', type: 'text', required: true, maxLength: 200 },
      { name: 'practitioner_name', label: 'Practitioner / contact', type: 'text', maxLength: 150 },
      { name: 'decision_maker', label: 'Decision-maker', type: 'text', maxLength: 200, placeholder: 'Owner / studio manager — ask on visit' },
      { name: 'modality', label: 'Category / modality', type: 'text', maxLength: 150 },
      { name: 'city_region', label: 'Area / route', type: 'text', maxLength: 100 },
      { name: 'address', label: 'Address', type: 'text', maxLength: 255 },
      { name: 'postcode', label: 'Postcode', type: 'text', maxLength: 16, placeholder: 'NW6 3AY' },
      { name: 'website', label: 'Website', type: 'url', maxLength: 255, placeholder: 'studio.co.uk' },
      { name: 'business_email', label: 'Email', type: 'email', maxLength: 255, help: 'One lead per email address. Cleared with an empty field.' },
      { name: 'phone', label: 'Phone', type: 'tel', maxLength: 40 },
      { name: 'instagram_handle', label: 'Instagram', type: 'text', maxLength: 100, placeholder: '@handle' },
      { name: 'facebook_url', label: 'Facebook', type: 'url', maxLength: 255 },
      { name: 'linkedin_url', label: 'LinkedIn', type: 'url', maxLength: 255 },
      { name: 'other_social', label: 'Other social', type: 'text', maxLength: 255, placeholder: 'TikTok @handle' },
    ],
  },
  {
    key: 'booking', title: 'Booking info', blurb: 'What they book with today and the evidence.',
    fields: [
      { name: 'booking_platform', label: 'Booking platform', type: 'text', maxLength: 100, placeholder: 'Unknown / Treatwell / Setmore …', help: 'Changing the platform name re-classifies the kind below unless you set the kind yourself (an in-house kind is never overridden).' },
      { name: 'booking_platform_kind', label: 'Platform kind', type: 'select', options: 'platformKinds' },
      { name: 'platform_evidence_note', label: 'Evidence note', type: 'textarea', maxLength: 500 },
      { name: 'platform_evidence_url', label: 'Evidence link', type: 'url', maxLength: 500 },
      { name: 'verification', label: 'Verification', type: 'text', maxLength: 80 },
      { name: 'links', label: 'Links', type: 'links', help: 'Maps / Google search / business source, plus public listing pages (one per line).' },
    ],
  },
  {
    key: 'sales', title: 'Sales', blurb: 'Where this prospect is in your pipeline.',
    fields: [
      { name: 'priority', label: 'Priority', type: 'select', options: 'priorities' },
      { name: 'route_day', label: 'Day / route', type: 'text', maxLength: 40 },
      { name: 'status', label: 'Sales status', type: 'select', options: 'statuses', required: true },
      { name: 'visit_approach', label: 'Visit approach', type: 'select', options: 'visitApproaches' },
      { name: 'visit_date', label: 'Visit date', type: 'date' },
      { name: 'outcome_note', label: 'Outcome / current pain', type: 'textarea' },
      { name: 'next_action', label: 'Next action', type: 'text', maxLength: 255 },
      { name: 'next_action_date', label: 'Next follow-up', type: 'date' },
      { name: 'subscribed', label: 'Subscribed?', type: 'bool' },
      { name: 'content_shoot_booked', label: 'Content shoot booked?', type: 'bool' },
      { name: 'fit_notes', label: 'Notes', type: 'textarea' },
      { name: 'campaign', label: 'Campaign', type: 'text', maxLength: 64, placeholder: 'booksesh-partners-week1-2026-10', help: 'Letters, digits, dot, dash, underscore.' },
    ],
  },
];

export const EDITABLE_FIELDS: ReadonlyArray<EditableField> = EDIT_SECTIONS.flatMap((s) => s.fields.map((f) => f.name));

/** Internal columns — never editable, never in a form, never on the wire (LeadProjection::INTERNAL_COLUMNS). */
export const INTERNAL_FIELDS: ReadonlyArray<string> = ['id', 'email_normalized', 'erasure_status', 'unsubscribe_token', 'ingest_idempotency_key', 'retention_purge_at', 'import_batch_id', 'dedup_name_postcode'];

const FIELD_DEFS: Record<string, EditFieldDef> = Object.fromEntries(EDIT_SECTIONS.flatMap((s) => s.fields.map((f) => [f.name, f])));

/** Human label for a field name (History view). Unknown names are shown as-is. */
export function fieldLabel(name: string): string {
  return FIELD_DEFS[name]?.label ?? name;
}

/** Draft = the lead's current editable values (strings trimmed, bools, links copied). */
export function leadToEditDraft(lead: LeadRecord): LeadEditPatch {
  const d: LeadEditPatch = {};
  for (const f of EDITABLE_FIELDS) {
    if (f === 'links') d.links = lead.links ? { ...lead.links, listings: lead.links.listings ? [...lead.links.listings] : undefined } : null;
    else if (f === 'subscribed' || f === 'content_shoot_booked') d[f] = lead[f] ?? null;
    else d[f] = (lead[f] as string | null | undefined) ?? null;
  }
  return d;
}

function normStr(v: string | null | undefined): string | null {
  const s = (v ?? '').trim();
  return s ? s : null;
}
function normLinks(l: LeadLinks | null | undefined): string | null {
  if (!l) return null;
  const out: LeadLinks = {};
  for (const k of ['maps', 'search', 'source'] as const) { const v = normStr(l[k]); if (v) out[k] = v; }
  const listings = (l.listings ?? []).map((u) => u.trim()).filter(Boolean);
  if (listings.length) out.listings = [...new Set(listings)];
  return Object.keys(out).length ? JSON.stringify(out) : null;
}

/** Only the fields whose value differs from the lead (null/'' treated as equal; links compared structurally). */
export function diffLeadEdit(lead: LeadRecord, draft: LeadEditPatch): LeadEditPatch {
  const out: LeadEditPatch = {};
  for (const f of EDITABLE_FIELDS) {
    if (!(f in draft)) continue;
    if (f === 'links') {
      const a = normLinks(lead.links); const b = normLinks(draft.links);
      if (a !== b) out.links = b ? (JSON.parse(b) as LeadLinks) : null;
    } else if (f === 'subscribed' || f === 'content_shoot_booked') {
      if ((draft[f] ?? null) !== (lead[f] ?? null)) out[f] = draft[f] ?? null;
    } else {
      const a = normStr(lead[f] as string | null); const b = normStr(draft[f]);
      if (a !== b) out[f] = b;
    }
  }
  return out;
}

const URL_HOST_RX = /^[a-z0-9.-]+\.[a-z]{2,}(?::\d{2,5})?(\/\S*)?$/i;
const POSTCODE_RX = /\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/i;
const DATE_RX = /^\d{4}-\d{2}-\d{2}$/;
const CAMPAIGN_RX = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export function isUrlLike(s: string, allowBareHost: boolean): boolean {
  const v = s.trim();
  if (!v || /\s/.test(v)) return false;
  if (/^https?:\/\//i.test(v)) { try { return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(new URL(v).hostname); } catch { return false; } }
  return allowBareHost && !/^[a-z][a-z0-9+.-]*:/i.test(v) && URL_HOST_RX.test(v);
}

/** Client-side mirror of the server rules; returns {field: message} for the fields in `patch`. */
export function validateLeadEdit(patch: LeadEditPatch): Partial<Record<EditableField, string>> {
  const e: Partial<Record<EditableField, string>> = {};
  const has = (k: EditableField) => k in patch;
  if (has('business_name') && !normStr(patch.business_name)) e.business_name = 'Business name is required.';
  if (has('status') && !normStr(patch.status)) e.status = 'Choose a status.';
  if (has('business_email') && patch.business_email && !EMAIL_RX.test(patch.business_email.trim())) e.business_email = 'Not a valid email address.';
  for (const k of ['website', 'platform_evidence_url', 'facebook_url', 'linkedin_url'] as const) {
    if (has(k) && patch[k] && !isUrlLike(patch[k] as string, true)) e[k] = 'Not a valid web address.';
  }
  if (has('postcode') && patch.postcode && !POSTCODE_RX.test(patch.postcode)) e.postcode = 'Not a UK postcode (e.g. NW6 3AY).';
  if (has('phone') && patch.phone && patch.phone.replace(/\D/g, '').length < 7) e.phone = 'Needs at least 7 digits.';
  for (const k of ['visit_date', 'next_action_date'] as const) {
    if (has(k) && patch[k] && !DATE_RX.test(patch[k] as string)) e[k] = 'Use the date picker (YYYY-MM-DD).';
  }
  if (has('campaign') && patch.campaign && !CAMPAIGN_RX.test(patch.campaign)) e.campaign = 'Letters, digits, dot, dash or underscore only (max 64).';
  if (has('booking_platform_kind') && patch.booking_platform_kind && !LEAD_OPTIONS.platformKinds.some((o) => o.value === patch.booking_platform_kind)) e.booking_platform_kind = 'Choose a platform kind.';
  if (has('visit_approach') && patch.visit_approach && !LEAD_OPTIONS.visitApproaches.some((o) => o.value === patch.visit_approach)) e.visit_approach = 'Choose a visit approach.';
  if (has('priority') && patch.priority && !LEAD_OPTIONS.priorities.some((o) => o.value === patch.priority)) e.priority = 'Choose A, B or C.';
  if (has('links') && patch.links) {
    const l = patch.links;
    for (const k of ['maps', 'search', 'source'] as const) { if (l[k] && !isUrlLike(l[k] as string, false)) e.links = `${k} must be a full http(s) link.`; }
    if ((l.listings ?? []).some((u) => u.trim() && !isUrlLike(u, false))) e.links = 'Every listing must be a full http(s) link.';
    if ((l.listings ?? []).length > 20) e.links = 'At most 20 listing links.';
  }
  for (const f of EDITABLE_FIELDS) {
    const def = FIELD_DEFS[f];
    if (def?.maxLength && has(f) && typeof patch[f as EditableTextField] === 'string' && (patch[f as EditableTextField] as string).length > def.maxLength) e[f] = `Too long (max ${def.maxLength} characters).`;
  }
  return e;
}

/** Enum-valued fields rendered with their labels in History (A3). */
const LABELLED_FIELDS: Record<string, keyof LeadOptionConfig> = { status: 'statuses', visit_approach: 'visitApproaches', booking_platform_kind: 'platformKinds', priority: 'priorities' };

/** Render one History change line: "Email: old → new" (enum tokens shown as their labels). */
export function describeChange(c: LeadChange): { label: string; from: string; to: string } {
  const list = LABELLED_FIELDS[c.field];
  const show = (v: string | null): string => (v === null || v === '' ? '—' : list ? (optionLabel(LEAD_OPTIONS[list], v, v) ?? v) : v);
  return { label: fieldLabel(c.field), from: show(c.old), to: show(c.new) };
}

/* ============================================================================
 * Platform filter by NAME — one <select> value encodes: `kind:<platform_kind>`
 * (Own website / In-house / Unknown — rest as is), `group:<directory|software>`
 * ("All …" options) or `name:<Vendor>` (one vendor). Names + counts come from the
 * server facets (platform_name); the grouping comes from the central config.
 * ========================================================================== */

export type PlatformFilterValue = '' | `kind:${string}` | `group:${PlatformGroup}` | `name:${string}`;

/** Decode the select value into list query fields (exactly one of platform / platform_group / platform_kind). */
export function platformFilterToQuery(value: string | null | undefined): Pick<LeadsQuery, 'platform' | 'platform_group' | 'platform_kind'> {
  const v = (value ?? '').trim();
  if (!v) return {};
  if (v.startsWith('kind:')) return { platform_kind: v.slice(5) };
  if (v.startsWith('group:')) return { platform_group: v.slice(6) };
  if (v.startsWith('name:')) return { platform: v.slice(5) };
  return {};
}

export interface PlatformFilterGroup { key: PlatformGroup | 'other'; title: string; options: Array<{ value: string; label: string; count?: number }> }

/** Build the grouped Platform filter from the server facets (names → counts, kinds → counts). */
export function platformFilterGroups(facets: LeadsPage['facets'] | undefined): PlatformFilterGroup[] {
  const names = facets?.platform_name ?? {};
  const kinds = facets?.booking_platform_kind ?? {};
  const groups = facets?.platform_group ?? {};
  const byGroup: Record<PlatformGroup, Array<{ value: string; label: string; count?: number }>> = { directory: [], software: [] };
  for (const name of Object.keys(names).sort((a, b) => a.localeCompare(b))) byGroup[platformGroup(name)].push({ value: `name:${name}`, label: name, count: names[name] });
  const out: PlatformFilterGroup[] = [];
  for (const g of ['directory', 'software'] as const) {
    const all = groups[g] ?? byGroup[g].reduce((n, o) => n + (o.count ?? 0), 0);
    out.push({ key: g, title: PLATFORM_GROUP_LABEL[g].title, options: [{ value: `group:${g}`, label: PLATFORM_GROUP_LABEL[g].all, count: all || undefined }, ...byGroup[g]] });
  }
  const rest: Array<{ value: string; label: string; count?: number }> = [];
  for (const k of ['direct_website', 'in_house_system', 'unknown'] as const) rest.push({ value: `kind:${k}`, label: PLATFORM_KIND_LABEL[k], count: kinds[k] });
  out.push({ key: 'other', title: 'Other', options: rest });
  return out;
}

/** Options for a facet-driven filter select (e.g. Day): every value the server facet lists, in server order,
 *  with its count — PLUS the currently selected value when the facet does not list it (other filters can leave
 *  it with 0 matches), so the select never silently shows "all" while a filter is still applied (item 8 D1). */
export function facetOptions(facet: Record<string, number> | undefined, selected?: string | null): Array<[string, string]> {
  const f = facet ?? {};
  const out: Array<[string, string]> = Object.keys(f).map((k) => [k, `${k} (${f[k]})`]);
  if (selected && !(selected in f)) out.push([selected, `${selected} (0)`]);
  return out;
}

/** Human description of the active filters (for the export audit/filename and the UI). */
export function describeFilters(q: Partial<LeadsQuery>): string {
  const parts: string[] = [];
  if (q.q) parts.push(`search "${q.q}"`);
  if (q.status) parts.push(statusLabel(q.status));
  if (q.route_day) parts.push(q.route_day);
  if (q.priority) parts.push(`Priority ${q.priority}`);
  if (q.approach) parts.push(approachLabel(q.approach) ?? q.approach);
  if (q.platform) parts.push(q.platform);
  if (q.platform_group) parts.push(PLATFORM_GROUP_LABEL[q.platform_group as PlatformGroup]?.all ?? q.platform_group);
  if (q.platform_kind) parts.push(platformKindLabel(q.platform_kind));
  if (q.category) parts.push(categoryName(q.category));
  return parts.join(' · ');
}

/* ============================================================================
 * Lead CATEGORIES (item 7) — the workspace list (GET /outreach/lead-categories)
 * and the pure helpers the filter, chips, edit picker and settings panel use.
 * ========================================================================== */

export interface LeadCategoryItem {
  slug: string;
  name: string;
  source: CategorySource;
  status: 'active' | 'archived';
  /** set when merged: every lead carrying this slug now reads as merged_into */
  merged_into?: string;
  /** workspace-wide lead count (list endpoint) */
  count?: number;
  /** a slug a lead carries that the list does not know (shown, never dropped) */
  unlisted?: boolean;
}
export interface LeadCategoryList {
  items: LeadCategoryItem[];
  is_default: boolean;
  /** false until migration 005 adds the categories column: manual edits are refused (categories still derived) */
  column_enabled: boolean;
  synced_at: string | null;
  sync_source: string | null;
  snapshot: { version: string; source: string; fetched_at: string; terms: number };
}

/** The central seed as a list (mock mode + fallback while the list loads). */
export function seedCategoryList(): LeadCategoryList {
  return {
    items: CATEGORY_DATA.seed.map((s) => ({ slug: s.slug, name: s.name, source: s.source, status: 'active' as const })),
    is_default: true, column_enabled: false, synced_at: null, sync_source: null,
    snapshot: { version: CATEGORY_DATA.snapshot.version, source: CATEGORY_DATA.snapshot.source, fetched_at: CATEGORY_DATA.snapshot.fetched_at, terms: CATEGORY_DATA.snapshot.terms.length },
  };
}

/** Display name of a slug (list name, else the seed name, else a title-cased slug — never dropped). */
export function categoryName(slug: string, list?: LeadCategoryList | null): string {
  return list?.items.find((i) => i.slug === slug)?.name
    ?? CATEGORY_DATA.seed.find((i) => i.slug === slug)?.name
    ?? slug.replace(/-/g, ' ').replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

/** Follow merged_into (bounded) to the surviving slug — mirror of LeadCategories::resolve. */
export function resolveCategory(slug: string, list?: LeadCategoryList | null): string {
  const seen = new Set<string>();
  let s = slug;
  while (!seen.has(s)) { seen.add(s); const it = list?.items.find((i) => i.slug === s); if (!it?.merged_into) return s; s = it.merged_into; }
  return s;
}

/** Display labels for the category sources (Settings › Lead categories badges). */
export const CATEGORY_SOURCE_LABEL: Record<string, string> = { booksesh: 'BookSesh', booksesh_events: 'BookSesh (events)', lead: 'Lead-derived', workspace: 'Added here', system: 'Fallback' };

/** Category filter options: every active (not merged) category plus any category that still has leads in view,
 *  in list order, with the facet count; the selected slug always stays listed. */
export function categoryFilterOptions(list: LeadCategoryList | null | undefined, facet: Record<string, number> | undefined, selected?: string | null): Array<[string, string]> {
  const f = facet ?? {};
  const items = list?.items ?? seedCategoryList().items;
  const out: Array<[string, string]> = [];
  const seen = new Set<string>();
  for (const it of items) {
    const n = f[it.slug] ?? 0;
    const visible = (it.status === 'active' && !it.merged_into) || n > 0 || it.slug === selected;
    if (!visible) continue;
    out.push([it.slug, `${it.name}${n ? ` (${n})` : ''}`]);
    seen.add(it.slug);
  }
  for (const slug of Object.keys(f)) if (!seen.has(slug)) { out.push([slug, `${categoryName(slug, list)} (${f[slug]})`]); seen.add(slug); }
  if (selected && !seen.has(selected)) out.push([selected, `${categoryName(selected, list)} (0)`]);
  return out;
}

/** Categories the edit picker offers: active, not merged (plus any the lead already carries). */
export function pickableCategories(list: LeadCategoryList | null | undefined, current: string[] = []): LeadCategoryItem[] {
  const items = list?.items ?? seedCategoryList().items;
  return items.filter((i) => (i.status === 'active' && !i.merged_into) || current.includes(i.slug));
}

/** The picker's starting value: the manual list, or null when the lead is on automatic. */
export function categoryDraftFor(lead: LeadRecord): string[] | null {
  return lead.categories_source === 'manual' ? [...(lead.categories ?? [])] : null;
}

/** What to send for categories: undefined = unchanged; [] = back to automatic; else the manual list. */
export function diffCategories(lead: LeadRecord, draft: string[] | null): string[] | undefined {
  const cur = categoryDraftFor(lead);
  if (draft === null) return cur === null ? undefined : [];
  const next = [...new Set(draft)];
  if (next.length === 0) return cur === null ? undefined : [];
  if (cur !== null && cur.length === next.length && cur.every((s, i) => s === next[i])) return undefined;
  return next;
}
